import { useAccessCheckQuery } from "app/services/api/authorisationApi";
import { RootState } from "app/services/store";
import { finishedAccessCheck } from "app/store/slices/overlay";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import useLogout from "./useLogout";
import { SessionService } from "app/services/session/SessionService";

export function useAccessCheck() {
  const { showNewToast } = useDisplayToast();
  const { logout } = useLogout();
  const dispatch = useDispatch();
  const lastAccessCheckRef = useRef<number>(0);

  const token = useSelector(
    (state: RootState) => state.authorisation?.auth?.token,
  );

  // Check if enough time has passed since last access check
  const shouldSkipAccessCheck = () => {
    return SessionService.shouldDebounceFocusAccessCheck(
      lastAccessCheckRef.current,
    );
  };

  const { data: accessData, isFetching } = useAccessCheckQuery(undefined, {
    refetchOnFocus: true,
    skip: !token || shouldSkipAccessCheck(),
  });

  const overrideAccessCheck = () => {
    // No check is needed if we do not have a token after loading
    dispatch(finishedAccessCheck());
  };

  // Update last check time when fetch completes
  useEffect(() => {
    if (!isFetching && accessData) {
      lastAccessCheckRef.current = Date.now();
    }
  }, [isFetching, accessData]);

  useEffect(() => {
    if (
      !isFetching &&
      accessData &&
      !accessData.is_offline &&
      (!accessData.geo_access || !accessData.ip_access)
    ) {
      // If the user does not have access, log them out and show an error message
      logout();
      showNewToast({
        type: "error",
        title: "Access Denied",
        message: "You do not have permission to access this site",
      });
      overrideAccessCheck();
    }
  }, [accessData, isFetching]);

  return { accessData, overrideAccessCheck };
}
